import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card, Button, Alert, Form } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { FaFileExcel, FaArrowLeft, FaDownload, FaUtensils } from "react-icons/fa";
import axios from "axios";
import "react-toastify/dist/ReactToastify.css";

const API_URL = process.env.REACT_APP_PYTHON_API_URL || "http://localhost:8003";

const ReporteMenusCiclicos = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const currentUser = JSON.parse(localStorage.getItem("user"));

  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);
  const [menusCiclicos, setMenusCiclicos] = useState([]);
  const [selectedMenuId, setSelectedMenuId] = useState("");
  const [loadingMenus, setLoadingMenus] = useState(true);

  // Redirect if not authenticated
  useEffect(() => {
    if (!token || !currentUser) {
      navigate("/");
    }
  }, [navigate, token, currentUser]);

  // Cargar lista de menús cíclicos
  useEffect(() => {
    const cargarMenusCiclicos = async () => {
      if (!token) return;
      
      try {
        setLoadingMenus(true);
        const response = await axios.get(
          `${API_URL}/api/mantenimientos/planificacion-insumos/menus-ciclicos/`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setMenusCiclicos(response.data || []);
      } catch (error) {
        console.error("Error al cargar menús cíclicos:", error);
        toast.error("Error al cargar la lista de menús cíclicos");
      } finally {
        setLoadingMenus(false);
      }
    };

    cargarMenusCiclicos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Solo se ejecuta una vez al montar el componente

  const descargarExcel = async () => {
    // Validar que se haya seleccionado un menú
    if (!selectedMenuId) {
      toast.error("Por favor seleccione un menú cíclico");
      return;
    }

    try {
      setIsGeneratingExcel(true);
      console.log('📊 Generando reporte de menús cíclicos...');

      const response = await axios.get(
        `${API_URL}/api/reportes/planificacion-insumos/menus-ciclicos/excel`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            menu_id: parseInt(selectedMenuId),
          },
          responseType: 'blob', // Importante para recibir el archivo
        }
      );

      // Crear blob y descargar
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Generar nombre del archivo
      const fecha = new Date().toISOString().split('T')[0];
      const filename = `reporte_menus_ciclicos_${fecha}.xlsx`;

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      console.log('✅ Excel descargado:', filename);
      toast.success("Reporte Excel descargado exitosamente");

    } catch (error) {
      console.error("❌ Error generando Excel:", error);
      const errorMsg = error.response?.data?.detail || "Error al generar el archivo Excel";
      toast.error(errorMsg);
    } finally {
      setIsGeneratingExcel(false);
    }
  };

  return (
    <>
      <style>
        {`
          .dh-maintenance-bg {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            min-height: 100vh;
            padding: 2rem 0;
          }
          .dh-card {
            border: none;
            border-radius: 20px;
            box-shadow: 0 15px 35px rgba(0,0,0,0.1);
            background: white;
          }
          .dh-card-header {
            border-radius: 15px 15px 0 0;
            padding: 1rem 1.5rem;
            border: none;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
          }
          .btn-dh-primary {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            border: none;
            color: white;
            border-radius: 10px;
            padding: 8px 16px;
            font-weight: 600;
            font-size: 0.875rem;
            box-shadow: 0 4px 14px rgba(17, 153, 142, 0.3);
            transition: all 0.3s ease;
          }
          .btn-dh-primary:hover {
            background: linear-gradient(135deg, #38ef7d 0%, #2dd4bf 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(17, 153, 142, 0.4);
            color: white;
          }
          .btn-dh-secondary {
            background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%);
            border: none;
            color: white;
            border-radius: 10px;
            padding: 8px 16px;
            font-weight: 600;
            font-size: 0.875rem;
            box-shadow: 0 4px 14px rgba(107, 114, 128, 0.3);
            transition: all 0.3s ease;
          }
          .btn-dh-secondary:hover {
            background: linear-gradient(135deg, #4b5563 0%, #374151 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(107, 114, 128, 0.4);
            color: white;
          }
          .icon-large {
            font-size: 4rem;
            color: #667eea;
            margin-bottom: 1.5rem;
          }
          .report-info {
            background: linear-gradient(135deg, #f0f4ff 0%, #e8f0fe 100%);
            border-radius: 15px;
            padding: 1.5rem;
            margin: 1rem 0;
          }
        `}
      </style>

      <div className="dh-maintenance-bg">
        <ToastContainer position="top-right" autoClose={3000} />

        <Container>
          <Row className="justify-content-center">
            <Col lg={10} xl={8}>
              <Card className="dh-card">
                <Card.Header className="dh-card-header">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                      <FaUtensils className="me-3" size={20} />
                      <h5 className="mb-0" style={{ fontSize: '0.95rem', fontWeight: 700 }}>Reporte de Menús Cíclicos</h5>
                    </div>
                    <Button
                      className="btn-dh-secondary"
                      onClick={() => navigate("/reportes")}
                    >
                      <FaArrowLeft className="me-2" />
                      Volver
                    </Button>
                  </div>
                </Card.Header>

                <Card.Body className="p-4">
                  <div className="text-center report-info">
                    <div className="mt-4">
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-bold" style={{ fontSize: '0.875rem' }}>
                          <FaUtensils className="me-2" />
                          Seleccionar Menú Cíclico <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                          value={selectedMenuId}
                          onChange={(e) => setSelectedMenuId(e.target.value)}
                          disabled={loadingMenus || isGeneratingExcel}
                          style={{
                            borderRadius: "10px",
                            border: "2px solid #e5e7eb",
                            padding: "8px 12px",
                            fontSize: "0.875rem",
                          }}
                        >
                          <option value="">
                            {loadingMenus ? "Cargando menús..." : "Seleccione un menú cíclico"}
                          </option>
                          {menusCiclicos
                            .filter((menu) => menu.activo !== false)
                            .map((menu) => (
                              <option key={menu.id} value={menu.id}>
                                {menu.codigo_interno} - {menu.nombre}
                              </option>
                            ))}
                        </Form.Select>
                        {menusCiclicos.length === 0 && !loadingMenus && (
                          <Form.Text className="text-muted" style={{ fontSize: '0.8rem' }}>
                            No hay menús cíclicos disponibles
                          </Form.Text>
                        )}
                      </Form.Group>

                      <Button
                        className="btn-dh-primary"
                        onClick={descargarExcel}
                        disabled={isGeneratingExcel || !selectedMenuId || loadingMenus}
                      >
                        {isGeneratingExcel ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Generando Excel...
                          </>
                        ) : (
                          <>
                            <FaDownload className="me-2" />
                            Descargar Reporte Excel
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

export default ReporteMenusCiclicos;
